/**
 * Stage1ShowingRoles.js
 *
 * Stage 1 – Shows player role and waits until all players press Continue.
 * ------------------------------------------------------------
 * 1️⃣ Displays an animated "Game Starting" intro
 * 2️⃣ Reveals the player's role (Crewmate / Impostor)
 * 3️⃣ Uses Firestore to sync all players; only continues when everyone clicks
 */

import React, { useEffect, useMemo, useState } from "react";
import { handleContinueClick, listenForAllReady } from "../helpers/stageProgressHelpers";

const Stage1ShowingRoles = ({ players, user, roomID, setStage }) => {
    const [showRole, setShowRole] = useState(false);
    const [hasClicked, setHasClicked] = useState(false);
    const stageNumber = 1; // For this specific component

    /**
     * 🧠 Determine current player's role safely
     */
    const role = useMemo(() => {
        if (!user || !players?.length) return null;
        const me = players.find(p => p.id === user.uid);
        if (!me) return null;
        return me.impostor ? "Impostor" : "Crewmate";
    }, [players, user]);

    /**
     * 🕵️‍♂️ Get all impostors' names if current player is an impostor
     */
    const impostorNames = useMemo(() => {
        if (!players?.length) return [];
        return players
            .filter(p => p.impostor)
            .map(p => p.displayName || p.name || "Unknown");
    }, [players]);

    /**
     * 🎬 Animate “Game Starting” screen for 3 seconds before showing the role.
     */
    useEffect(() => {
        const timer = setTimeout(() => setShowRole(true), 3000);
        return () => clearTimeout(timer);
    }, []);

    /**
     * 🔁 Listen for all players to press Continue.
     */
    useEffect(() => {
        if (!roomID || !players?.length) return;
        const unsub = listenForAllReady(roomID, players.length, stageNumber, setStage);
        return () => unsub && unsub();
    }, [roomID, players, setStage]);

    /**
     * 🖱 Handle local Continue button click.
     */
    const handleClick = async () => {
        if (hasClicked) return;
        await handleContinueClick(roomID, user, stageNumber);
        setHasClicked(true);
    };
    
    return (
        <div className="w-full min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 sm:px-8 py-10 overflow-hidden">
            {/* STEP 1: Animated intro */}
            {!showRole && (
                <div className="flex flex-col items-center justify-center text-center animate-fade-in">
                    <h2 className="text-3xl sm:text-5xl font-bold tracking-wide text-white mb-8 animate-pulse">
                        Game&nbsp;Starting...
                    </h2>

                    {/* Expanding rings animation */}
                    <div className="relative flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40">
                        <div className="absolute w-6 h-6 bg-white rounded-full animate-ping"></div>
                        <div className="absolute w-16 h-16 border-4 border-white rounded-full animate-ping [animation-delay:0.4s]"></div>
                        <div className="absolute w-24 h-24 border-2 border-zinc-400 rounded-full animate-ping [animation-delay:0.8s]"></div>
                        <div className="absolute w-3 h-3 bg-sky-400 rounded-full animate-bounce"></div>
                    </div>

                    <p className="mt-6 text-zinc-400 text-sm sm:text-base">
                        Syncing players... establishing trust protocols 🤖
                    </p>
                </div>
            )}

            {/* STEP 2: Role Reveal */}
            {showRole && (
                <div className="flex flex-col items-center justify-center text-center animate-fade-in-slow">
                    <p className="text-lg sm:text-2xl text-zinc-300 font-medium mb-2">
                        You are the
                    </p>

                    <h1
                        className="text-5xl sm:text-7xl font-extrabold mb-4 tracking-wider break-words"
                        style={{
                            textShadow:
                                role === "Impostor"
                                    ? "0 0 25px rgba(255, 0, 0, 0.6)"
                                    : "0 0 25px rgba(56,189,248,0.5)",
                        }}
                    >
                        {role || "Fetching..."}
                    </h1>

                    {/* 👥 Show impostor names only to impostors */}
                    {role === "Impostor" && (
                        <div className="mt-4">
                            <p className="text-sm text-zinc-400 mb-1">Your fellow impostors:</p>
                            <ul className="text-red-400 text-lg font-medium space-y-1">
                                {impostorNames.map((name, idx) => (
                                    <li key={idx}>{name}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Continue Button */}
                    <button
                        onClick={handleClick}
                        disabled={hasClicked}
                        className="mt-8 px-10 py-2 sm:py-3 border border-white rounded-lg text-white text-base sm:text-lg font-medium hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {hasClicked ? "Waiting for others..." : "Continue"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default Stage1ShowingRoles;